import { useCallback, useMemo, useRef, useState } from 'react'
import Joyride, { Step, CallBackProps } from 'react-joyride'
import { Typography, Checkbox } from '@material-tailwind/react'
import { useTranslation } from 'react-i18next'
import { useSessionContext } from '../contexts/SessionContext'
import { useDarkMode } from '../contexts/DarkModeContext'
import { getTutorialStyle } from '../utils/constants/onboarding.constants'
import { setSkipOnboarding } from '../utils/helpers/supabase.helpers'
import { useGeneralToast } from '../hooks/toast'

export const createOnboardingSteps = (
  DontShowCheckbox: React.ReactNode,
  t: ReturnType<typeof useTranslation>['t']
): Step[] => [
  {
    content: (
      <>
        <Typography variant="h3">{t('onboarding.title')}</Typography>
        <Typography className="mt-4">{t('onboarding.description')}</Typography>
        {DontShowCheckbox}
      </>
    ),
    locale: { skip: t('onboarding.skip') },
    placement: 'center',
    target: 'body',
  },
  {
    content: <Typography>{t('onboarding.dashboardStep')}</Typography>,
    target: 'a[data-testid="dashboard-nav-link"]',
  },
  {
    content: <Typography>{t('onboarding.exploreStep')}</Typography>,
    target: 'a[data-testid="explore-nav-link"]',
  },
  {
    content: <Typography>{t('onboarding.uploadStep')}</Typography>,
    placement: 'left',
    target: 'a[data-testid="upload-nav-link"]',
  },
]

const OnboardingTutorial: React.FC = () => {
  const { t } = useTranslation()
  const { loading, user } = useSessionContext()
  const { darkMode } = useDarkMode()
  const savedSkip = useRef(false)
  const [dontShowAgain, setDontShowAgain] = useState(false)
  const { success } = useGeneralToast()

  const runTutorial = useMemo(
    () => Boolean(!loading && user?.id && user?.finalized && !user?.skip_onboarding),
    [loading, user]
  )

  const steps = useMemo(
    () =>
      createOnboardingSteps(
        <Checkbox
          label={<Typography>{t('onboarding.dontShowAgain')}</Typography>}
          color="blue-gray"
          checked={dontShowAgain}
          onChange={(e) => setDontShowAgain(e.currentTarget.checked)}
          type="checkbox"
        />,
        t
      ),
    [dontShowAgain, t]
  )

  const tutorialStyles = useMemo(() => getTutorialStyle(darkMode), [darkMode])

  const handleTutorialCallback = useCallback(
    async (e: CallBackProps) => {
      if (e.action === 'skip' && dontShowAgain && !savedSkip.current && user?.id) {
        // avoid double event
        savedSkip.current = true
        // save to DB
        await setSkipOnboarding({
          userId: user.id,
        })
        // toast user
        success(t('onboarding.successMessage'))
      }
    },
    [user?.id, dontShowAgain, savedSkip.current, success, t]
  )

  return (
    <div>
      <Joyride
        steps={steps}
        styles={tutorialStyles}
        run={runTutorial}
        continuous
        showProgress
        showSkipButton
        callback={handleTutorialCallback}
      />
    </div>
  )
}

export default OnboardingTutorial
